import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, RefreshCw, Cpu, HardDrive, Layers3, Loader2, Moon, Plus, Snowflake, Zap } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/utils/cn";
import { omnimizerApi, omnimizerKeys } from "../api";
import { useOmnimizerDeployments } from "../catalog-sync";
import { HOT_SWITCH_GPU_MEMORY_UTILIZATION, MODEL_CATALOG } from "../catalog";
import { errorMessage, lifecycleApi, sleepCapable, transitionStep } from "../lifecycle";
import { allocatedGpus, deploymentState } from "../state";
import type { OmnimizerCluster, OmnimizerDeployment } from "../types";
import { InfoTip } from "./InfoTip";
import { LifecycleActions } from "./LifecycleActions";

const CONFIRM_KEY = "omnimizer.fastSwitch.confirm";

function readConfirm() {
  try {
    return localStorage.getItem(CONFIRM_KEY) !== "false";
  } catch {
    return true;
  }
}

/** Host RAM held by a sleeping model: its weights, from the catalog when known. */
export function weightsGb(d: Pick<OmnimizerDeployment, "model_url">) {
  return MODEL_CATALOG.find((m) => m.hfPath === d.model_url)?.weightsGb ?? null;
}

export function gpuShare(d: OmnimizerDeployment) {
  const value = d.args?.llm && "gpu_memory_utilization" in d.args.llm
    ? (d.args.llm as { gpu_memory_utilization?: number | null }).gpu_memory_utilization
    : null;
  return value ?? (sleepCapable(d) ? HOT_SWITCH_GPU_MEMORY_UTILIZATION : null);
}

const fastLoad = (d: OmnimizerDeployment) =>
  (d.args?.llm as { load_format?: string } | null | undefined)?.load_format === "runai_streamer";

/** Split a cluster's Dynamo deployments into the lanes of the switch board. */
export function switchBoard(items: OmnimizerDeployment[], clusterId: string | undefined) {
  const rows = items.filter((d) => d.is_active && d.args?.runtime === "dynamo-vllm" && (!clusterId || d.cluster_id === clusterId));
  const holding = rows.filter((d) => ["ready", "inProgress"].includes(deploymentState(d)));
  const warm = rows.filter((d) => deploymentState(d) === "sleeping");
  const cold = rows.filter((d) => deploymentState(d) === "paused");
  const ram = warm.reduce(
    (acc, d) => {
      const gb = weightsGb(d);
      return gb == null ? { ...acc, unknown: acc.unknown + 1 } : { ...acc, gb: acc.gb + gb };
    },
    { gb: 0, unknown: 0 },
  );
  return {
    rows,
    holding,
    warm,
    cold,
    // A model going to sleep or stopping is already handing its GPU over: don't show 2 / 1 mid-switch.
    gpusHeld: holding
      .filter((d) => !(d.status === "STARTED" && ["SLEEPING", "PAUSING"].includes(d.current_step ?? "")))
      .reduce((sum, d) => sum + allocatedGpus(d), 0),
    hotModels: rows.filter(sleepCapable).length,
    ram,
  };
}

function Stat({ icon: Icon, label, value, info }: { icon: LucideIcon; label: string; value: string; info: string }) {
  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4 text-primary" aria-hidden />
        {label}
        <InfoTip label={label} className="ms-auto">{info}</InfoTip>
      </div>
      {/* Ratios and units read left-to-right in Arabic too ("3 / 4", "≈ 3.1 GB"). */}
      <strong dir="ltr" className="self-start text-2xl font-semibold tabular-nums [unicode-bidi:isolate]">{value}</strong>
    </Card>
  );
}

function Chip({ label, info }: { label: string; info: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 py-0.5 ps-2.5 pe-1 text-xs text-muted-foreground">
      {label}
      <InfoTip label={label}>{info}</InfoTip>
    </span>
  );
}

function ModelSettings({ d }: { d: OmnimizerDeployment }) {
  const { t } = useTranslation();
  const share = gpuShare(d);
  return (
    <div className="flex flex-wrap gap-1.5">
      {sleepCapable(d) ? (
        <Chip label={t("omnimizer.fastSwitch.chips.sleep")} info={t("omnimizer.fastSwitch.info.sleep")} />
      ) : (
        <Chip label={t("omnimizer.fastSwitch.chips.noSleep")} info={t("omnimizer.fastSwitch.info.noSleep")} />
      )}
      {share != null && (
        <Chip label={t("omnimizer.fastSwitch.chips.share", { percent: Math.round(share * 100) })} info={t("omnimizer.fastSwitch.info.share")} />
      )}
      {fastLoad(d) && <Chip label={t("omnimizer.fastSwitch.chips.fastLoad")} info={t("omnimizer.fastSwitch.info.fastLoad")} />}
    </div>
  );
}

function ModelTitle({ d, icon: Icon, tone }: { d: OmnimizerDeployment; icon: LucideIcon; tone: string }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className={cn("grid size-10 shrink-0 place-items-center rounded-xl", tone)}>
        <Icon className="size-5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block truncate font-semibold">{d.name}</span>
        <span className="block truncate text-xs text-muted-foreground">{d.model_url || d.namespace}</span>
      </span>
    </div>
  );
}

export function FastSwitchPage({
  clusterId,
  onCluster,
  canSwitch,
}: {
  clusterId?: string;
  onCluster: (id: string) => void;
  canSwitch: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Faster polling here: a wake is confirmed in seconds.
  const deployments = useOmnimizerDeployments(true, 3000);
  const clusters = useQuery({ queryKey: omnimizerKeys.clusters, queryFn: omnimizerApi.listClusters, refetchInterval: 30000 });
  const [confirm, setConfirm] = useState(readConfirm);
  const [target, setTarget] = useState<OmnimizerDeployment | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const items = deployments.data || [];

  const candidates = useMemo(() => {
    const used = new Set((deployments.data || []).filter((d) => d.args?.runtime === "dynamo-vllm").map((d) => d.cluster_id));
    return (clusters.data || []).filter((c) => used.has(c.id));
  }, [clusters.data, deployments.data]);
  const cluster: OmnimizerCluster | undefined =
    candidates.find((c) => c.id === clusterId) || candidates[0];
  const board = switchBoard(items, cluster?.id);
  const capacity = cluster?.max_gpu_count ?? null;

  function toggleConfirm(value: boolean) {
    setConfirm(value);
    try {
      localStorage.setItem(CONFIRM_KEY, String(value));
    } catch {
      // Preference only; the page works without storage.
    }
  }

  // What a switch to `d` releases, mirroring the API's plan (services/gpu_admission.plan_release).
  function releasePlan(d: OmnimizerDeployment) {
    const need = allocatedGpus({ ...d, status: "SUCCESS", current_step: "COMPLETED" });
    const free = capacity == null ? Infinity : capacity - board.gpusHeld;
    if (need <= free) return [];
    const ordered = [...board.holding].sort((a, b) => Number(sleepCapable(b)) - Number(sleepCapable(a)));
    const plan: OmnimizerDeployment[] = [];
    let freed = 0;
    for (const h of ordered) {
      if (freed >= need - free) break;
      plan.push(h);
      freed += allocatedGpus(h);
    }
    return plan;
  }

  async function switchTo(d: OmnimizerDeployment) {
    setTarget(null);
    setSwitching(d.model_id);
    try {
      const { data } = await lifecycleApi.switchTo(d.model_id);
      toast.success(
        t("omnimizer.lifecycle.switching", { name: d.name, released: data.released.map((r) => r.name).join(", ") || "—" }),
      );
      await queryClient.invalidateQueries({ queryKey: omnimizerKeys.deployments });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSwitching(null);
    }
  }

  const requestSwitch = (d: OmnimizerDeployment) => (confirm && releasePlan(d).length ? setTarget(d) : void switchTo(d));
  const plan = target ? releasePlan(target) : [];
  const wizardSearch = { hotSwitch: true as const, cluster: cluster?.id };

  if (deployments.isPending || clusters.isPending)
    return <p className="text-sm text-muted-foreground" role="status">{t("omnimizer.common.loading")}</p>;

  const SwitchButton = ({ d }: { d: OmnimizerDeployment }) => {
    const busy = switching === d.model_id || !!transitionStep(d);
    return (
      <Button size="sm" disabled={!canSwitch || busy || !!switching} onClick={() => requestSwitch(d)}>
        {busy ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden /> : <Zap aria-hidden />}
        {t("omnimizer.fastSwitch.switchHere")}
      </Button>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {(deployments.error || clusters.error) && (
        <p role="alert" className="rounded-lg border border-destructive bg-[var(--destructive-100)] p-3 text-sm text-foreground">
          {(deployments.error || clusters.error)?.message}
        </p>
      )}

      <Card className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        <ol className="flex flex-wrap items-center gap-2 text-sm" aria-label={t("omnimizer.fastSwitch.howTitle")}>
          {(
            [
              ["awake", Zap, "bg-primary/15 text-primary"],
              ["asleep", Moon, "bg-chart-2/15 text-chart-2"],
              ["wake", RefreshCw, "bg-muted text-foreground"],
            ] as const
          ).map(([key, Icon, tone], i) => (
            <li key={key} className="flex items-center gap-2">
              {i > 0 && <ArrowRight className="size-4 text-muted-foreground rtl:-scale-x-100" aria-hidden />}
              <span className={cn("grid size-8 place-items-center rounded-lg", tone)}>
                <Icon className="size-4" aria-hidden />
              </span>
              <span>
                <span className="block font-medium">{t(`omnimizer.fastSwitch.how.${key}.title`)}</span>
                <span className="block text-xs text-muted-foreground">{t(`omnimizer.fastSwitch.how.${key}.body`)}</span>
              </span>
            </li>
          ))}
        </ol>
        {candidates.length > 1 && (
          <div role="radiogroup" aria-label={t("omnimizer.fastSwitch.cluster")} className="flex flex-wrap gap-1 rounded-full border border-border bg-muted/30 p-1">
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={c.id === cluster?.id}
                onClick={() => onCluster(c.id)}
                className={cn(
                  "min-h-9 rounded-full px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  c.id === cluster?.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </Card>

      {!cluster ? (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <Layers3 className="size-8 text-primary" aria-hidden />
          <h2 className="text-base font-semibold">{t("omnimizer.fastSwitch.emptyTitle")}</h2>
          <p className="max-w-md text-sm text-muted-foreground">{t("omnimizer.fastSwitch.emptyBody")}</p>
          {canSwitch && (
            <Button asChild>
              <Link to="/deployments/new" search={{ hotSwitch: true }}>
                <Plus aria-hidden />
                {t("omnimizer.fastSwitch.addModel")}
              </Link>
            </Button>
          )}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              icon={Cpu}
              label={t("omnimizer.fastSwitch.stats.awake")}
              value={capacity ? `${board.gpusHeld} / ${capacity}` : String(board.gpusHeld)}
              info={t("omnimizer.fastSwitch.info.awake")}
            />
            <Stat icon={Moon} label={t("omnimizer.fastSwitch.stats.warm")} value={String(board.warm.length)} info={t("omnimizer.fastSwitch.info.warm")} />
            <Stat
              icon={HardDrive}
              label={t("omnimizer.fastSwitch.stats.ram")}
              value={board.warm.length ? `≈ ${board.ram.gb.toFixed(1)} GB${board.ram.unknown ? " +" : ""}` : "0 GB"}
              info={t("omnimizer.fastSwitch.info.ram")}
            />
            <Stat icon={Zap} label={t("omnimizer.fastSwitch.stats.hot")} value={`${board.hotModels} / ${board.rows.length}`} info={t("omnimizer.fastSwitch.info.hot")} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="flex flex-col gap-4">
              <Card className="gap-0 overflow-hidden border-primary/40 p-0">
                <header className="flex items-center gap-2 border-b border-border bg-primary/5 px-5 py-3">
                  <span className="size-2 rounded-full bg-primary motion-safe:animate-pulse" aria-hidden />
                  <h2 className="text-sm font-semibold">{t("omnimizer.fastSwitch.onGpu")}</h2>
                  <InfoTip label={t("omnimizer.fastSwitch.onGpu")}>{t("omnimizer.fastSwitch.info.onGpu")}</InfoTip>
                </header>
                {board.holding.length ? (
                  <ul className="divide-y divide-border">
                    {board.holding.map((d) => (
                      <li key={d.model_id} className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex min-w-0 flex-col gap-2">
                          <ModelTitle d={d} icon={Zap} tone="bg-primary/15 text-primary" />
                          <ModelSettings d={d} />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {deploymentState(d) === "ready" && (
                            <Badge variant="success" className="gap-1.5">
                              <span className="size-1.5 rounded-full bg-current" aria-hidden />
                              {t("omnimizer.fastSwitch.serving")}
                            </Badge>
                          )}
                          {canSwitch && <LifecycleActions deployment={d} />}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-5 py-6 text-sm text-muted-foreground">{t("omnimizer.fastSwitch.gpuFree")}</p>
                )}
              </Card>

              <Card className="gap-0 overflow-hidden p-0">
                <header className="flex items-center gap-2 border-b border-border px-5 py-3">
                  <h2 className="text-sm font-semibold">{t("omnimizer.fastSwitch.ready")}</h2>
                  <InfoTip label={t("omnimizer.fastSwitch.ready")}>{t("omnimizer.fastSwitch.info.ready")}</InfoTip>
                </header>
                {board.warm.length + board.cold.length ? (
                  <ul className="divide-y divide-border">
                    {[...board.warm, ...board.cold].map((d) => {
                      const warm = deploymentState(d) === "sleeping";
                      return (
                        <li key={d.model_id} className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between">
                          <div className="flex min-w-0 flex-col gap-2">
                            <ModelTitle d={d} icon={warm ? Moon : Snowflake} tone={warm ? "bg-chart-2/15 text-chart-2" : "bg-muted text-muted-foreground"} />
                            <ModelSettings d={d} />
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              {warm ? t("omnimizer.fastSwitch.warm") : t("omnimizer.fastSwitch.cold")}
                              <InfoTip label={warm ? t("omnimizer.fastSwitch.warm") : t("omnimizer.fastSwitch.cold")}>
                                {warm ? t("omnimizer.fastSwitch.info.warmRow") : t("omnimizer.fastSwitch.info.coldRow")}
                              </InfoTip>
                            </span>
                            {canSwitch && <SwitchButton d={d} />}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="px-5 py-6 text-sm text-muted-foreground">{t("omnimizer.fastSwitch.noneReady")}</p>
                )}
              </Card>
            </div>

            <aside className="flex flex-col gap-4">
              <Card className="flex flex-col gap-4 p-5">
                <h2 className="text-sm font-semibold">{t("omnimizer.fastSwitch.settings")}</h2>
                <div className="flex items-start gap-3">
                  <Switch id="fast-switch-confirm" checked={confirm} onCheckedChange={toggleConfirm} />
                  <label htmlFor="fast-switch-confirm" className="flex-1 text-sm">
                    <span className="flex items-center gap-1 font-medium">
                      {t("omnimizer.fastSwitch.confirm")}
                    </span>
                    <span className="block text-xs text-muted-foreground">{t("omnimizer.fastSwitch.confirmHint")}</span>
                  </label>
                  <InfoTip label={t("omnimizer.fastSwitch.confirm")}>{t("omnimizer.fastSwitch.info.confirm")}</InfoTip>
                </div>
                {canSwitch && (
                  <Button asChild variant="outline">
                    <Link to="/deployments/new" search={wizardSearch}>
                      <Plus aria-hidden />
                      {t("omnimizer.fastSwitch.addModel")}
                    </Link>
                  </Button>
                )}
              </Card>
              <Card className="flex flex-col gap-3 p-5">
                <h2 className="text-sm font-semibold">{t("omnimizer.fastSwitch.requirements")}</h2>
                <dl className="flex flex-col gap-2.5 text-sm">
                  {(
                    [
                      ["sharing", t("omnimizer.fastSwitch.req.sharingValue")],
                      ["capacity", capacity ? t("omnimizer.fastSwitch.req.capacityValue", { count: capacity }) : t("omnimizer.fastSwitch.req.capacityUnknown")],
                      ["level", t("omnimizer.fastSwitch.req.levelValue")],
                      ["share", `${Math.round(HOT_SWITCH_GPU_MEMORY_UTILIZATION * 100)}%`],
                      ["streamer", t("omnimizer.fastSwitch.req.streamerValue")],
                    ] as const
                  ).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <dt className="flex items-center gap-1 text-muted-foreground">
                        {t(`omnimizer.fastSwitch.req.${key}`)}
                        <InfoTip label={t(`omnimizer.fastSwitch.req.${key}`)}>{t(`omnimizer.fastSwitch.info.req.${key}`)}</InfoTip>
                      </dt>
                      <dd className="text-end font-medium tabular-nums">{value}</dd>
                    </div>
                  ))}
                </dl>
              </Card>
            </aside>
          </div>
        </>
      )}

      <AlertDialog open={!!target} onOpenChange={(open) => !open && setTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("omnimizer.fastSwitch.confirmTitle", { name: target?.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t("omnimizer.fastSwitch.confirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="flex flex-col gap-1.5 text-sm">
            {plan.map((h) => (
              <li key={h.model_id} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                <span className="min-w-0 truncate font-medium">{h.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {sleepCapable(h) && deploymentState(h) === "ready" ? t("omnimizer.lifecycle.willSleep") : t("omnimizer.lifecycle.willStop")}
                </span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("omnimizer.lifecycle.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => target && void switchTo(target)}>
              {t("omnimizer.fastSwitch.switchHere")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
